import { observer } from 'mobx-react-lite';

import { filePreviewPanelStore } from '@/features/chat/file-preview-panel.store';
import { userStore } from '@/stores/user.store';

import { sandboxTerminalPanelStore } from './sandbox-terminal-panel.store';
import { SandboxTerminal } from './SandboxTerminal';
import { TerminalDock } from './TerminalDock';
import { TerminalErrorBoundary } from './TerminalErrorBoundary';

interface SandboxTerminalPanelProps {
  /** Chat surface this layout shows; the panel renders only when the store is bound to it. */
  chatId?: string;
  /**
   * When true, dock as an absolute bottom overlay (used in the chat thread, whose
   * row already fills the outlet — overlaying keeps the chat height unchanged) and,
   * if the file preview is open, match its width and right-dock under it. When false
   * (default), render as an inline full-width flex row that shrinks the surface above
   * it (used in the sprite view, which has a flex column and a dev-panel footer).
   */
  overlay?: boolean;
}

/**
 * Bottom-docked host for the internal sandbox terminal. Rendered at the bottom of
 * the chat thread and sprite layouts; visibility lives in sandboxTerminalPanelStore
 * (opened from the Sandbox terminal toggle). The local terminal has its own
 * app-wide panel (LocalTerminalPanel).
 */
export const SandboxTerminalPanel = observer(function SandboxTerminalPanel({
  chatId,
  overlay = false,
}: SandboxTerminalPanelProps) {
  // Internal-only: this panel is mounted in the chat thread for everyone, so guard
  // directly rather than relying solely on the (internal-gated) open toggle.
  const isOpen =
    userStore.isInternalUser &&
    chatId !== undefined &&
    sandboxTerminalPanelStore.isSandboxOpenFor(chatId);

  if (!isOpen || chatId === undefined) return null;

  const previewShowing = overlay && filePreviewPanelStore.isOpen;
  const matchPreviewWidth = previewShowing && !filePreviewPanelStore.isFloating;
  const previewFloating = previewShowing && filePreviewPanelStore.isFloating;

  return (
    <TerminalDock
      overlay={overlay}
      width={matchPreviewWidth ? filePreviewPanelStore.dockedWidth : undefined}
      alignRight={matchPreviewWidth}
      inset={previewShowing}
      insetLeft={previewFloating}
      testId="sandbox-terminal-panel"
    >
      <TerminalErrorBoundary onError={() => sandboxTerminalPanelStore.close()}>
        {/* keyed: a chat switch is a new session, not a prop change on a live one */}
        <SandboxTerminal
          key={chatId}
          chatId={chatId}
          onClose={() => sandboxTerminalPanelStore.close()}
        />
      </TerminalErrorBoundary>
    </TerminalDock>
  );
});
